import React, { useState, useEffect } from 'react';
import { 
 CheckCircle, XCircle, MessageSquare, LogOut, User, Calendar, 
 Clock, ZoomIn, ZoomOut, RotateCcw, AlertTriangle, ThumbsUp, Eye,
 Star, RefreshCw, Search, Users, Database, TrendingUp, Play, Pause,
 Save, Upload, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Query, ID } from 'appwrite';

// Import des services APPWRITE
import { focalApiService } from '../../services/focalApiService';
import { 
 auditSessionService, 
 auditTaskService, 
 auditorService,
 authService,
 databases
} from '../../services/appwriteService';

const appwriteDatabaseId = process.env.REACT_APP_APPWRITE_DATABASE_ID || 'focal-db';

const truncateUrl = (url: string, maxLength: number = 2900): string => {
 if (!url) return '';
 if (url.length <= maxLength) return url;
 
 console.log(`🔗 URL trop longue (${url.length} chars), troncature à ${maxLength}`);
 
 // Garder le début et la fin de l'URL pour qu'elle reste utilisable
 const start = url.substring(0, maxLength - 100);
 const end = url.substring(url.length - 50);
 const truncated = `${start}...${end}`;
 
 console.log(`🔗 URL tronquée: ${truncated.length} chars`);
 return truncated;
};

// ✅ Interface pour les props
interface AuditorInterfaceProps {
 onBackToDashboard?: () => void;
 onSessionStateChange?: (isActive: boolean) => void;
}

// Types adaptés pour Appwrite
interface Agent {
 email: string;
 displayName: string;
 userId: string;
}

interface ParsedTask {
 match_task_id: number;
 date: string;
 state: string;
 email: string;
 priority_names?: string;
 issue_names?: string;
 processing_time_seconds: number;
 template_unpaired: number;
 template_total: number;
 plano_unpaired: number;
 plano_total: number;
 template_image_urls: string[];
 plano_image_urls: string[];
 model_drawn_template_urls: string[];
 agent_submitted_urls: string[];
 model_template_used: number;
 unlock_draw_mode_used: number;
 task_type: 'pair_only' | 'complete_match';
 // Nouveaux champs Appwrite
 appwrite_task_id?: string;
 audit_status?: 'pending' | 'approved' | 'rejected';
 audit_comment?: string;
}

interface AuditSession {
 $id?: string;
 auditor_id: string;
 agent_email: string;
 audit_date: string;
 total_tasks: number;
 completed_tasks: number;
 approved_tasks: number;
 rejected_tasks: number;
 session_status: 'in_progress' | 'completed' | 'paused';
 session_type?: string;
 $createdAt?: string;
 $updatedAt?: string;
}

interface AuditResult {
 taskId: number;
 appwriteTaskId?: string;
 taskType: 'pair_only' | 'complete_match';
 result: 'validated' | 'commented';
 comment?: string;
 severity?: 'minor' | 'major' | 'critical';
 timestamp: string;
 auditorEmail: string;
}

// ✅ Composant ImageViewer CORRIGÉ - Stop boucle d'erreur
const ImageViewer: React.FC<{
 src: string;
 alt: string;
 title: string;
 className?: string;
}> = ({ src, alt, title, className = '' }) => {
 const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
 const [isDragging, setIsDragging] = useState(false);
 const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
 const [imageError, setImageError] = useState(false); // ✅ AJOUT état erreur

 const handleZoomIn = (e: React.MouseEvent) => {
   e.stopPropagation();
   setTransform(prev => ({ ...prev, scale: Math.min(prev.scale * 1.3, 3) }));
 };

 const handleZoomOut = (e: React.MouseEvent) => {
   e.stopPropagation();
   setTransform(prev => ({ ...prev, scale: Math.max(prev.scale / 1.3, 0.5) }));
 };

 const handleReset = (e: React.MouseEvent) => {
   e.stopPropagation();
   setTransform({ scale: 1, x: 0, y: 0 });
 };

 const handleMouseDown = (e: React.MouseEvent) => {
   if (transform.scale > 1) {
     setIsDragging(true);
     setDragStart({ x: e.clientX - transform.x, y: e.clientY - transform.y });
   }
 };

 const handleMouseMove = (e: React.MouseEvent) => {
   if (isDragging && transform.scale > 1) {
     setTransform(prev => ({
       ...prev,
       x: e.clientX - dragStart.x,
       y: e.clientY - dragStart.y
     }));
   }
 };

 const handleMouseUp = () => {
   setIsDragging(false);
 };

 // ✅ CORRIGER la gestion d'erreur pour éviter la boucle
 const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
   console.error('❌ Erreur chargement image:', src);
   setImageError(true); // Marquer comme erreur pour éviter les rechargements
 };

 return (
   <div className={`relative group h-full ${className}`}>
     <div className="absolute top-4 left-4 z-30 bg-black/90 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-xl">
       {title}
     </div>

     <div className="absolute top-4 right-4 z-30 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
       <button onClick={handleZoomIn} className="bg-blue-600 text-white p-2 rounded-xl hover:bg-blue-700 shadow-xl">
         <ZoomIn className="w-5 h-5" />
       </button>
       <button onClick={handleZoomOut} className="bg-purple-600 text-white p-2 rounded-xl hover:bg-purple-700 shadow-xl">
         <ZoomOut className="w-5 h-5" />
       </button>
       <button onClick={handleReset} className="bg-red-600 text-white p-2 rounded-xl hover:bg-red-700 shadow-xl">
         <RotateCcw className="w-5 h-5" />
       </button>
     </div>

     <div 
       className="relative h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden cursor-move shadow-xl border border-gray-200"
       onMouseDown={handleMouseDown}
       onMouseMove={handleMouseMove}
       onMouseUp={handleMouseUp}
       onMouseLeave={handleMouseUp}
     >
       {/* ✅ AFFICHAGE CONDITIONNEL selon l'état d'erreur */}
       {imageError || !src ? (
         <div className="w-full h-full flex items-center justify-center text-gray-500">
           <div className="text-center">
             <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-50" />
             <p className="text-lg font-medium">Image non disponible</p>
             <p className="text-sm opacity-70">{title}</p>
             <p className="text-xs opacity-50 mt-2 px-4 break-all">URL: {src?.substring(0, 100)}...</p>
           </div>
         </div>
       ) : (
         <img
           src={src}
           alt={alt}
           className="w-full h-full object-contain transition-transform duration-200"
           style={{
             transform: `scale(${transform.scale}) translate(${transform.x / transform.scale}px, ${transform.y / transform.scale}px)`,
             cursor: transform.scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
           }}
           onError={handleImageError} // ✅ UTILISER la nouvelle fonction
         />
       )}
     </div>

     {transform.scale !== 1 && !imageError && (
       <div className="absolute bottom-4 left-4 z-30 bg-indigo-600 text-white px-3 py-2 rounded-xl text-sm font-bold shadow-xl">
         {Math.round(transform.scale * 100)}%
       </div>
     )}
   </div>
 );
};

// 🆕 NOUVEAU COMPOSANT: PlanoImageViewer avec navigation multiple images
const PlanoImageViewer: React.FC<{
 images: string[];
 title: string;
 className?: string;
}> = ({ images, title, className = '' }) => {
 const [currentImageIndex, setCurrentImageIndex] = useState(0);
 const [isTransitioning, setIsTransitioning] = useState(false);

 // Reset index si changement d'images
 useEffect(() => {
   setCurrentImageIndex(0);
 }, [images]);

 // Gestion des raccourcis clavier
 useEffect(() => {
   const handleKeyPress = (e: KeyboardEvent) => {
     if (images.length <= 1) return;
     
     if (e.key === 'ArrowLeft') {
       e.preventDefault();
       setCurrentImageIndex(prev => prev > 0 ? prev - 1 : images.length - 1);
     } else if (e.key === 'ArrowRight') {
       e.preventDefault();
       setCurrentImageIndex(prev => prev < images.length - 1 ? prev + 1 : 0);
     }
   };

   window.addEventListener('keydown', handleKeyPress);
   return () => window.removeEventListener('keydown', handleKeyPress);
 }, [images.length]);

 const handleTabClick = (index: number) => {
   if (index === currentImageIndex) return;
   
   setIsTransitioning(true);
   setCurrentImageIndex(index);
   
   // Animation de transition
   setTimeout(() => setIsTransitioning(false), 300);
 };

 // ✅ DEBUG - Log des images reçues
 console.log(`🖼️ PlanoImageViewer - ${title}:`, {
   imagesCount: images.length,
   images: images.map((img, i) => `${i+1}: ${img.substring(0, 100)}...`)
 });

 if (!images || images.length === 0) {
   return (
     <div className={`h-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border border-gray-300 ${className}`}>
       <div className="text-center text-gray-500">
         <Eye className="w-16 h-16 mx-auto mb-4 opacity-30" />
         <p className="text-lg font-medium">Pas d'image plano</p>
         <p className="text-sm opacity-70">Aucune URL extraite</p>
       </div>
     </div>
   );
 }

 const currentImage = images[currentImageIndex];
 const hasMultipleImages = images.length > 1;

 return (
   <div className={`relative h-full flex flex-col ${className} group`}>
     {/* ✅ DEBUG INFO en haut */}
     <div className="absolute top-1 right-1 z-50 bg-purple-600 text-white text-xs px-2 py-1 rounded">
       {images.length} plano(s)
     </div>

     {/* 🎯 ONGLETS PREMIUM avec animations */}
     {hasMultipleImages && (
       <div className="flex-shrink-0 mb-4">
         {/* Onglets principaux */}
         <div className="relative">
           <div className="flex space-x-2 bg-gradient-to-r from-purple-50 to-pink-50 p-2 rounded-2xl border border-purple-100 shadow-lg">
             {images.map((_, index) => (
               <button
                 key={index}
                 onClick={() => handleTabClick(index)}
                 className={`relative flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 transform ${
                   index === currentImageIndex
                     ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105 z-10'
                     : 'text-purple-600 hover:text-purple-700 hover:bg-white/70 hover:shadow-md hover:scale-102'
                 }`}
               >
                 <div className="flex items-center justify-center gap-2">
                   {/* Icône personnalisée selon l'index */}
                   <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                     index === currentImageIndex 
                       ? 'bg-white shadow-lg' 
                       : 'bg-purple-400'
                   }`} />
                   <span>Plano {index + 1}</span>
                   
                   {/* Badge "Actuel" */}
                   {index === currentImageIndex && (
                     <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                   )}
                 </div>
                 
                 {/* Effet de survol */}
                 <div className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${
                   index === currentImageIndex 
                     ? 'opacity-0' 
                     : 'opacity-0 hover:opacity-10 bg-purple-500'
                 }`} />
               </button>
             ))}
           </div>
           
           {/* Barre de progression */}
           <div className="mt-3 flex justify-center">
             <div className="flex space-x-1">
               {images.map((_, index) => (
                 <div
                   key={index}
                   className={`h-1 rounded-full transition-all duration-500 ${
                     index === currentImageIndex
                       ? 'w-8 bg-gradient-to-r from-purple-500 to-pink-500'
                       : 'w-2 bg-gray-300'
                   }`}
                 />
               ))}
             </div>
           </div>
         </div>
         
         {/* Info complémentaire */}
         <div className="mt-3 text-center">
           <div className="inline-flex items-center gap-2 text-xs text-gray-600 bg-white px-3 py-1 rounded-full shadow border">
             <Eye className="w-3 h-3" />
             <span>Image {currentImageIndex + 1} sur {images.length}</span>
             {isTransitioning && (
               <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" />
             )}
           </div>
         </div>
       </div>
     )}

     {/* Header avec titre (si une seule image) */}
     {!hasMultipleImages && (
       <div className="absolute top-2 left-2 z-40">
         <div className="bg-black/90 text-white px-3 py-2 rounded-xl text-sm font-bold shadow-xl">
           {title}
         </div>
       </div>
     )}

     {/* Image principale avec transition */}
     <div className={`flex-1 min-h-0 transition-opacity duration-300 ${
       isTransitioning ? 'opacity-75' : 'opacity-100'
     }`}>
       <ImageViewer
         src={currentImage}
         alt={`${title} ${currentImageIndex + 1}`}
         title={hasMultipleImages ? `${title} ${currentImageIndex + 1}/${images.length}` : title}
         className="h-full"
       />
     </div>

     {/* Badge status en bas */}
     {hasMultipleImages && (
       <div className="absolute bottom-4 right-4 z-40">
         <div className="bg-gradient-to-r from-purple-600/90 to-pink-600/90 text-white text-xs px-3 py-2 rounded-xl backdrop-blur-sm border border-white/20 shadow-lg">
           <div className="flex items-center gap-2">
             <div className="flex space-x-0.5">
               {images.map((_, index) => (
                 <div
                   key={index}
                   className={`w-1.5 h-1.5 rounded-full transition-colors ${
                     index === currentImageIndex ? 'bg-white' : 'bg-white/40'
                   }`}
                 />
               ))}
             </div>
             <span>Plano {currentImageIndex + 1}</span>
           </div>
         </div>
       </div>
     )}
   </div>
 );
};

// ✅ FONCTION D'EXTRACTION AVEC DEBUG COMPLET
const extractImageUrlsFixed = (imageHtml: string | null): string[] => {
 if (!imageHtml) {
   console.log('⚠️ extractImageUrlsFixed - imageHtml est vide ou null');
   return [];
 }
 
 console.log('🔍 === EXTRACTION DEBUG ===');
 console.log('📏 Longueur HTML:', imageHtml.length);
 console.log('📋 Contenu HTML (premiers 300 chars):', imageHtml.substring(0, 300));
 
 const urls: string[] = [];
 
 // 🎯 EXTRACTION des SRC d'images avec DEBUG
 const imgSrcRegex = /<img[^>]+src="([^"]+)"/g;
 let match;
 let matchCount = 0;
 
 while ((match = imgSrcRegex.exec(imageHtml)) !== null) {
   matchCount++;
   const url = match[1];
   console.log(`📸 SRC ${matchCount} trouvé:`, url.substring(0, 150) + '...');
   
   if (url && url.includes('focal.systems') && url.length > 50) {
     console.log(`✅ SRC ${matchCount} ajouté`);
     urls.push(url);
   } else {
     console.log(`❌ SRC ${matchCount} rejeté - focal.systems: ${url.includes('focal.systems')}, longueur: ${url.length}`);
   }
 }
 
 console.log(`🔍 Total matches img src: ${matchCount}`);
 
 // 🎯 Si pas d'images, essayer href avec DEBUG
 if (urls.length === 0) {
   console.log('🔍 Aucune image src valide, essai href...');
   const hrefRegex = /<a[^>]+href="([^"]+)"/g;
   let hrefCount = 0;
   
   while ((match = hrefRegex.exec(imageHtml)) !== null) {
     hrefCount++;
     const url = match[1];
     console.log(`🔗 HREF ${hrefCount} trouvé:`, url.substring(0, 150) + '...');
     
     if (url && url.includes('focal.systems') && (url.includes('/draw?') || url.includes('.png') || url.includes('.jpg'))) {
       console.log(`✅ HREF ${hrefCount} ajouté`);
       urls.push(url);
     }
   }
   console.log(`🔍 Total matches href: ${hrefCount}`);
 }
 
 // Supprimer doublons
 const uniqueUrls = [...new Set(urls)];
 
 console.log(`🖼️ === RÉSULTAT FINAL: ${uniqueUrls.length} URLs ===`);
 uniqueUrls.forEach((url, i) => {
   console.log(`  ${i+1}. ${url.substring(0, 150)}...`);
 });
 console.log('=== FIN EXTRACTION ===\n');
 
 return uniqueUrls;
};

// ✅ PARSING AVEC DEBUG ISSUES COMPLET
const parseAuditDataWithFixedExtraction = (rawData: any[], taskType: 'pair_only' | 'complete_match'): ParsedTask[] => {
 console.log(`🔄 === PARSING ${taskType.toUpperCase()} AVEC DEBUG ISSUES ===`);
 
 if (!Array.isArray(rawData) || rawData.length === 0) {
   console.warn('⚠️ Données invalides ou vides');
   return [];
 }

 // 🔍 DEBUG: Analyser les ISSUES dans les données
 if (rawData[0]) {
   const firstTask = rawData[0];
   console.log('🔍 === DEBUG ISSUES PREMIÈRE TÂCHE ===');
   console.log('📋 issue_names:', firstTask.issue_names);
   console.log('📋 issue_labels:', firstTask.issue_labels);
   console.log('📋 Type de tâche:', taskType);
   console.log('📋 Champ utilisé:', taskType === 'pair_only' ? 'issue_labels' : 'issue_names');
   console.log('📋 Toutes les clés disponibles:', Object.keys(firstTask));
 }

 return rawData.map((task: any, index: number) => {
   try {
     console.log(`\n🔄 Processing tâche ${index + 1}: ${task.match_task_id} (${taskType})`);
     
     let templateImageUrls: string[] = [];
     let agentSubmittedUrls: string[] = [];
     let modelDrawnTemplateUrls: string[] = [];
     let issueNames = '';

     if (taskType === 'pair_only') {
      agentSubmittedUrls = extractImageUrlsFixed(task.template_image);
      modelDrawnTemplateUrls = [task.model_drawn_template].filter(Boolean);
      templateImageUrls = [];
      
      // ✅ CORRECTION: Pour Pair Only, chercher issue_labels en priorité, sinon issue_names
      issueNames = task.issue_labels || task.issue_names || '';
      console.log(`📋 Pair Only - issue_labels: "${task.issue_labels}", issue_names: "${task.issue_names}", utilisé: "${issueNames}"`);
    } else {
      templateImageUrls = extractImageUrlsFixed(task.template_image);
      agentSubmittedUrls = [];
      modelDrawnTemplateUrls = [];
      
      // ✅ COMPLETE MATCH: chercher issue_names en priorité, sinon issue_labels
      issueNames = task.issue_names || task.issue_labels || '';
      console.log(`📋 Complete Match - issue_names: "${task.issue_names}", issue_labels: "${task.issue_labels}", utilisé: "${issueNames}"`);
    }

     // 🔧 EXTRACTION PLANO avec DEBUG DÉTAILLÉ
     console.log(`🖼️ === EXTRACTION PLANO pour tâche ${task.match_task_id} ===`);
     console.log(`📋 plano_image existe: ${!!task.plano_image}`);
     console.log(`📋 plano_image longueur: ${task.plano_image?.length || 0}`);
     if (task.plano_image) {
       console.log(`📋 plano_image preview: ${task.plano_image.substring(0, 200)}...`);
     }
     
     const planoImageUrls = extractImageUrlsFixed(task.plano_image);
     console.log(`✅ Plano URLs extraites: ${planoImageUrls.length}`);

     const parsed: ParsedTask = {
       match_task_id: task.match_task_id || 0,
       date: task.utc_time_task_submitted || task.date || new Date().toISOString(),
       state: task.state || 'unknown',
       email: task.email || 'unknown@email.com',
       priority_names: task.priority_names || '',
       issue_names: issueNames, // ✅ CHAMP UNIFIÉ
       processing_time_seconds: task.processing_time_seconds || 0,
       template_unpaired: task.template_unpaired || 0,
       template_total: task.template_total || 0,
       plano_unpaired: Math.floor(task.plano_unpaired || 0),
       plano_total: Math.floor(task.plano_total || 0),
       template_image_urls: templateImageUrls,
       plano_image_urls: planoImageUrls,
       model_drawn_template_urls: modelDrawnTemplateUrls,
       agent_submitted_urls: agentSubmittedUrls,
       model_template_used: task.model_template_used || 0,
       unlock_draw_mode_used: task.unlock_draw_mode_used || 0,
       task_type: taskType
     };

     // Log résumé avec ISSUES
     console.log(`📋 Tâche ${task.match_task_id} parsée:`, {
       taskType,
       issues: parsed.issue_names ? `"${parsed.issue_names}"` : 'VIDE',
       planoImages: parsed.plano_image_urls.length,
       templateImages: parsed.template_image_urls.length,
       agentSubmitted: parsed.agent_submitted_urls.length,
       modelTemplate: parsed.model_drawn_template_urls.length
     });

     return parsed;
   } catch (error) {
     console.error(`💥 Erreur parsing tâche ${task.match_task_id}:`, error);
     return null;
   }
 }).filter(Boolean) as ParsedTask[];
};

// Composant TaskCard avec DEBUG amélioré
const TaskCard: React.FC<{
 task: ParsedTask;
 onValidate: () => void;
 onComment: (comment: string, severity: 'minor' | 'major' | 'critical') => void;
 isCurrentTask: boolean;
 savingToAppwrite: boolean;
}> = ({ task, onValidate, onComment, isCurrentTask, savingToAppwrite }) => {
 const [showCommentForm, setShowCommentForm] = useState(false);
 const [comment, setComment] = useState('');
 const [severity, setSeverity] = useState<'minor' | 'major' | 'critical'>('minor');

 const handleSubmitComment = () => {
   if (comment.trim()) {
     onComment(comment, severity);
     setComment('');
     setShowCommentForm(false);
   }
 };

 const formatMadagascarTime = (utcDateString: string) => {
   const utcDate = new Date(utcDateString);
   const madagascarTime = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000));
   
   const date = madagascarTime.toLocaleDateString('fr-FR', {
     day: '2-digit',
     month: '2-digit', 
     year: 'numeric'
   });
   
   const time = madagascarTime.toLocaleTimeString('fr-FR', {
     hour: '2-digit',
     minute: '2-digit',
     second: '2-digit'
   });
   
   return { date, time, full: `${date} à ${time}` };
 };

 // ✅ DEBUG LOG TaskCard
 console.log(`🔍 TaskCard Debug pour tâche ${task.match_task_id}:`, {
   taskType: task.task_type,
   issue_names: task.issue_names,
   has_issues: !!(task.issue_names && task.issue_names.trim()),
   issue_length: task.issue_names?.length || 0,
   plano_count: task.plano_image_urls.length,
   template_count: task.template_image_urls.length,
   agent_count: task.agent_submitted_urls.length
 });

 if (!isCurrentTask) {
   return (
     <div className="bg-white/50 rounded-2xl p-8 border border-gray-200 opacity-50 h-[90vh]">
       <div className="text-center text-gray-500 h-full flex items-center justify-center">
         <div>
           <Clock className="w-12 h-12 mx-auto mb-4" />
           <p className="text-lg font-medium">Tâche en attente</p>
           <p className="text-sm">ID: {task.match_task_id}</p>
           {task.appwrite_task_id && (
             <p className="text-xs text-green-600 mt-1">✓ Sauvegardée</p>
           )}
         </div>
       </div>
     </div>
   );
 }

 return (
   <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden min-h-[90vh] flex flex-col">
     {/* Header avec statut Appwrite */}
     <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white p-6 flex-shrink-0">
       <div className="flex justify-between items-start mb-4">
         <div>
           <h2 className="text-2xl font-bold">{task.task_type === 'pair_only' ? 'Pair Only Task' : 'Complete Match Task'}</h2>
           <p className="text-blue-100">ID: {task.match_task_id}</p>
           {task.appwrite_task_id && (
             <div className="flex items-center gap-2 mt-1">
               <Save className="w-4 h-4 text-green-300" />
               <span className="text-green-200 text-sm">Sauvegardé en DB</span>
             </div>
           )}
         </div>
         <div className="text-right text-sm">
           <div className="flex items-center gap-2 text-blue-100 mb-1">
             <User className="w-4 h-4" />
             <span>{task.email}</span>
           </div>
           <div className="flex items-center gap-2 text-blue-100 mb-1">
             <Calendar className="w-4 h-4" />
             <span>{formatMadagascarTime(task.date).full}</span>
           </div>
           <div className="flex items-center gap-2 text-blue-100">
             <Clock className="w-4 h-4" />
             <span>{Math.floor(task.processing_time_seconds / 60)}m {task.processing_time_seconds % 60}s</span>
           </div>
         </div>
       </div>

{/* ✅ Issues et Priorités avec DEBUG */}
{task.issue_names && task.issue_names.trim() && (
         <div className="mb-4 p-4 bg-white/15 rounded-xl border-2 border-yellow-300/50 backdrop-blur-sm">
           <div className="flex items-start gap-3">
             <div className="bg-yellow-400/20 p-2 rounded-lg">
               <AlertTriangle className="w-6 h-6 text-yellow-200 flex-shrink-0" />
             </div>
             <div className="flex-1">
             <h3 className="font-bold text-yellow-100 mb-3 text-lg">
              🚨 {task.task_type === 'pair_only' ? 'Issues détectés (issue_labels)' : 'Issues identifiés '}
            </h3>
               
               {/* AFFICHAGE TAGS ISSUES - TRÈS VISIBLE */}
               <div className="flex flex-wrap gap-2 mb-3">
                 {task.issue_names.split(',').map((issue, index) => (
                   <span 
                     key={index}
                     className="bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 px-4 py-2 rounded-xl text-sm font-bold border-2 border-yellow-300 shadow-lg transform hover:scale-105 transition-all"
                   >
                     🔥 {issue.trim()}
                   </span>
                 ))}
               </div>
               
             </div>
             
             {task.task_type === 'complete_match' && task.priority_names && task.priority_names.trim() && (
               <div className="flex items-center gap-2 ml-4">
                 <Star className="w-4 h-4 text-orange-300" />
                 <span className="font-bold text-orange-200 text-sm">Priorité:</span>
                 <span className="bg-orange-500/20 text-orange-100 px-2 py-1 rounded text-sm font-medium">
                   {task.priority_names}
                 </span>
               </div>
             )}
           </div>
         </div>
       )}

       {/* ✅ DEBUG - Si pas d'issues, afficher pourquoi */}
       {(!task.issue_names || !task.issue_names.trim()) && (
         <div className="mb-4 p-3 bg-gray-500/20 rounded-xl border border-gray-400/30">
           <div className="text-gray-300 text-sm">
             🔍  Aucun label déclaré pour {task.task_type}
             <br />
             • issue_names: "{task.issue_names || 'vide'}"
             <br />
             • task_type: {task.task_type}
           </div>
         </div>
       )}

       {task.task_type === 'pair_only' && task.priority_names && task.priority_names.trim() && (
         <div className="mb-4 p-3 bg-white/10 rounded-xl border border-white/20 backdrop-blur-sm">
           <div className="flex items-center gap-2">
             <Star className="w-4 h-4 text-orange-300" />
             <span className="font-bold text-orange-200 text-sm">Priorité:</span>
             <span className="bg-orange-500/20 text-orange-100 px-2 py-1 rounded text-sm font-medium">
               {task.priority_names}
             </span>
           </div>
         </div>
       )}

       {/* Status et stats */}
       <div className="flex items-center justify-between">
         <div className="flex items-center gap-4">
           <span className={`px-3 py-1 rounded-xl text-sm font-bold ${
             task.state === 'completed' ? 'bg-green-500/20 text-green-100' : 'bg-red-500/20 text-red-100'
           }`}>
             {task.state === 'completed' ? <CheckCircle className="w-4 h-4 inline mr-1" /> : <XCircle className="w-4 h-4 inline mr-1" />}
             {task.state.toUpperCase()}
           </span>
           
           <div className="flex gap-3 text-xs">
             <span className="bg-indigo-500/20 text-indigo-100 px-2 py-1 rounded-lg">
               Model Template: {task.model_template_used ? '✓' : '✗'}
             </span>
             <span className="bg-pink-500/20 text-pink-100 px-2 py-1 rounded-lg">
               Draw Mode: {task.unlock_draw_mode_used ? '✓' : '✗'}
             </span>
           </div>
         </div>
         
         <div className="flex gap-4 text-sm">
           <span className="bg-blue-500/20 text-blue-100 px-3 py-1 rounded-xl">
             Template: {task.template_total - task.template_unpaired}/{task.template_total}
           </span>
           <span className="bg-purple-500/20 text-purple-100 px-3 py-1 rounded-xl">
             Plano: {task.plano_total - task.plano_unpaired}/{task.plano_total}
           </span>
         </div>
       </div>
     </div>

     {/* Images section */}
     <div className="flex-1 p-6 overflow-hidden">
       <div className={`grid gap-6 h-full ${task.task_type === 'pair_only' ? 'grid-cols-3' : 'grid-cols-2'}`}>
         {/* Model Template (pour pair_only seulement) */}
         {task.task_type === 'pair_only' && (
           <div className="flex flex-col h-full">
             <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 shadow-lg border border-blue-100 mb-4 flex-shrink-0">
               <h3 className="font-bold text-blue-800 text-lg mb-2">Model Template</h3>
               <p className="text-blue-600 text-sm">Modèle de référence</p>
               <div className="grid grid-cols-2 gap-3 mt-3">
                 <div className="bg-white rounded-lg p-3 text-center shadow border">
                   <div className="text-2xl font-bold text-blue-600">{task.template_total}</div>
                   <div className="text-xs text-gray-600">Total</div>
                 </div>
                 <div className="bg-white rounded-lg p-3 text-center shadow border">
                   <div className="text-2xl font-bold text-orange-600">{task.template_unpaired}</div>
                   <div className="text-xs text-gray-600">Non-pairées</div>
                 </div>
               </div>
               <div className="mt-3 bg-gray-200 rounded-full h-2">
                 <div 
                   className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-700"
                   style={{ 
                     width: `${task.template_total > 0 ? ((task.template_total - task.template_unpaired) / task.template_total) * 100 : 0}%` 
                   }}
                 />
               </div>
             </div>
             
             <div className="flex-1 min-h-0">
               {task.model_drawn_template_urls && task.model_drawn_template_urls.length > 0 ? (
                 <ImageViewer
                   src={task.model_drawn_template_urls[0]}
                   alt="Model Template"
                   title="Model Template"
                   className="h-full"
                 />
               ) : (
                 <div className="h-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border border-gray-300">
                   <div className="text-center text-gray-500">
                     <Eye className="w-16 h-16 mx-auto mb-4 opacity-30" />
                     <p className="text-lg font-medium">Pas de Model Template</p>
                     <p className="text-xs opacity-50">URLs: {task.model_drawn_template_urls.length}</p>
                   </div>
                 </div>
               )}
             </div>
           </div>
         )}

         {/* Agent Submitted / Template Image */}
         <div className="flex flex-col h-full">
           <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 shadow-lg border border-green-100 mb-4 flex-shrink-0">
             <h3 className="font-bold text-green-800 text-lg mb-2">
               {task.task_type === 'pair_only' ? 'Agent Submitted' : 'Template Image'}
             </h3>
             <p className="text-green-600 text-sm">
               {task.task_type === 'pair_only' ? 'Image soumise par l\'agent' : 'Image de référence'}
             </p>
             <div className="mt-2 text-xs text-green-700">
               Agent: {task.email}
             </div>
             {/* ✅ DEBUG INFO */}
             <div className="mt-1 text-xs text-green-800 bg-green-100 p-1 rounded">
               🔍 URLs: {task.task_type === 'pair_only' ? task.agent_submitted_urls.length : task.template_image_urls.length}
             </div>
             
             {task.task_type === 'complete_match' && (
               <>
                 <div className="grid grid-cols-2 gap-3 mt-3">
                   <div className="bg-white rounded-lg p-3 text-center shadow border">
                     <div className="text-2xl font-bold text-green-600">{task.template_total}</div>
                     <div className="text-xs text-gray-600">Total</div>
                   </div>
                   <div className="bg-white rounded-lg p-3 text-center shadow border">
                     <div className="text-2xl font-bold text-orange-600">{task.template_unpaired}</div>
                     <div className="text-xs text-gray-600">Non-pairées</div>
                   </div>
                 </div>
                 <div className="mt-3 bg-gray-200 rounded-full h-2">
                   <div 
                     className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-700"
                     style={{ 
                       width: `${task.template_total > 0 ? ((task.template_total - task.template_unpaired) / task.template_total) * 100 : 0}%` 
                     }}
                   />
                 </div>
               </>
             )}
           </div>
           
           <div className="flex-1 min-h-0">
             {task.task_type === 'pair_only' ? (
               // Pour pair_only: afficher agent_submitted_urls
               task.agent_submitted_urls && task.agent_submitted_urls.length > 0 ? (
                 <ImageViewer
                   src={task.agent_submitted_urls[0]}
                   alt="Agent Submitted"
                   title="Agent Submitted"
                   className="h-full"
                 />
               ) : (
                 <div className="h-full bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-green-300">
                   <div className="text-center text-green-600">
                     <Database className="w-16 h-16 mx-auto mb-4 opacity-50" />
                     <p className="text-lg font-medium">Agent Submitted</p>
                     <p className="text-sm opacity-70">Pas d'image disponible</p>
                     <p className="text-xs opacity-50">URLs: {task.agent_submitted_urls.length}</p>
                   </div>
                 </div>
               )
             ) : (
               // Pour COMPLETE_MATCH: afficher template_image_urls
               task.template_image_urls && task.template_image_urls.length > 0 ? (
                 <ImageViewer
                   src={task.template_image_urls[0]}
                   alt="Template"
                   title="Template Image"
                   className="h-full"
                 />
               ) : (
                 <div className="h-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border border-gray-300">
                   <div className="text-center text-gray-500">
                     <Eye className="w-16 h-16 mx-auto mb-4 opacity-30" />
                     <p className="text-lg font-medium">Pas d'image template</p>
                     <p className="text-xs opacity-50">URLs: {task.template_image_urls.length}</p>
                   </div>
                 </div>
               )
             )}
           </div>
         </div>

         {/* ✅ Plano - SUPPORT MULTIPLE IMAGES POUR TOUS LES TYPES */}
         <div className="flex flex-col h-full">
           <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 shadow-lg border border-purple-100 mb-4 flex-shrink-0">
             <h3 className="font-bold text-purple-800 text-lg mb-2">
               Plano Images ({task.plano_image_urls.length})
             </h3>
             
             {/* ✅ DEBUG INFO - Visible temporairement */}
             <div className="text-xs text-purple-700 bg-purple-100 p-2 rounded mb-2">
               🔍 DEBUG: {task.plano_image_urls.length} URLs plano extraites
             </div>
             
             <div className="grid grid-cols-2 gap-3">
               <div className="bg-white rounded-lg p-3 text-center shadow border">
                 <div className="text-2xl font-bold text-purple-600">{task.plano_total}</div>
                 <div className="text-xs text-gray-600">Total</div>
               </div>
               <div className="bg-white rounded-lg p-3 text-center shadow border">
                 <div className="text-2xl font-bold text-orange-600">{task.plano_unpaired}</div>
                 <div className="text-xs text-gray-600">Non-pairées</div>
               </div>
             </div>
             <div className="mt-3 bg-gray-200 rounded-full h-2">
               <div 
                 className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-700"
                 style={{ 
                   width: `${task.plano_total > 0 ? ((task.plano_total - task.plano_unpaired) / task.plano_total) * 100 : 0}%` 
                 }}
               />
             </div>
           </div>
           
           <div className="flex-1 min-h-0">
             {/* ✅ TOUJOURS utiliser PlanoImageViewer pour gérer les multiples */}
             <PlanoImageViewer
               images={task.plano_image_urls}
               title="Plano"
               className="h-full"
             />
           </div>
         </div>
       </div>
     </div>

     {/* Actions section SIMPLIFIEES */}
     <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 flex-shrink-0 max-h-96 overflow-y-auto">
       {!showCommentForm ? (
         <div className="flex gap-6 justify-center w-full">
           <button
             onClick={onValidate}
             disabled={savingToAppwrite}
             className="flex items-center gap-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:from-green-600 hover:to-emerald-600 transform hover:scale-105 transition-all duration-300 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
           >
             {savingToAppwrite ? (
               <>
                 <RefreshCw className="w-6 h-6 animate-spin" />
                 Sauvegarde...
               </>
             ) : (
               <>
                 <ThumbsUp className="w-6 h-6" />
                 Valider la tâche
               </>
             )}
           </button>
           <button
             onClick={() => setShowCommentForm(true)}
             disabled={savingToAppwrite}
             className="flex items-center gap-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:from-orange-600 hover:to-red-600 transform hover:scale-105 transition-all duration-300 shadow-xl disabled:opacity-50"
           >
             <MessageSquare className="w-6 h-6" />
             Ajouter un commentaire
           </button>
         </div>
       ) : (
         <div className="space-y-4 w-full">
           <div className="flex justify-between items-center">
             <h3 className="text-xl font-bold">Commentaire d'audit</h3>
             <button onClick={() => setShowCommentForm(false)} className="text-gray-500 hover:text-gray-700">
               <XCircle className="w-5 h-5" />
             </button>
           </div>

           {/* Sévérité */}
           <div className="grid grid-cols-3 gap-4">
             {[
               { value: 'minor', label: 'Minor', color: 'bg-yellow-500 border-yellow-600' },
               { value: 'major', label: 'Major', color: 'bg-orange-500 border-orange-600' },
               { value: 'critical', label: 'Critical', color: 'bg-red-500 border-red-600' }
             ].map((sev) => (
               <button
                 key={sev.value}
                 onClick={() => setSeverity(sev.value as any)}
                 className={`p-4 rounded-xl border-2 text-white font-bold transition-all transform ${
                   severity === sev.value
                     ? `${sev.color} scale-105 shadow-xl`
                     : 'bg-white border-gray-300 text-gray-700 hover:shadow-lg'
                 }`}
               >
                 {sev.label}
               </button>
             ))}
           </div>

           <textarea
             value={comment}
             onChange={(e) => setComment(e.target.value)}
             placeholder="Décrivez le problème détecté..."
             className="w-full p-4 border border-gray-300 rounded-xl resize-none text-lg"
             rows={4}
           />

           <div className="flex gap-4">
             <button
               onClick={handleSubmitComment}
               disabled={!comment.trim() || savingToAppwrite}
               className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl font-bold hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
             >
               {savingToAppwrite ? (
                 <div className="flex items-center justify-center gap-2">
                   <RefreshCw className="w-4 h-4 animate-spin" />
                   Sauvegarde...
                 </div>
               ) : (
                 'Envoyer le commentaire'
               )}
             </button>
             <button
               onClick={() => setShowCommentForm(false)}
               disabled={savingToAppwrite}
               className="px-6 py-3 bg-gray-300 text-gray-700 rounded-xl hover:bg-gray-400 transition-colors font-bold disabled:opacity-50"
             >
               Annuler
             </button>
           </div>
         </div>
       )}
     </div>
   </div>
 );
};

const ProgressIndicator: React.FC<{ tasks: ParsedTask[] }> = ({ tasks }) => {
  // Helpers pour le comptage (utiliser les mêmes que dans le composant principal)
  const isPending = (t: ParsedTask) => !t.audit_status || t.audit_status === 'pending';
  const isDone = (t: ParsedTask) => t.audit_status === 'approved' || t.audit_status === 'rejected';
  
  const pendingCount = tasks.filter(isPending).length;
  const completedCount = tasks.filter(isDone).length;
  const approvedCount = tasks.filter(t => t.audit_status === 'approved').length;
  const rejectedCount = tasks.filter(t => t.audit_status === 'rejected').length;
  const totalTasks = tasks.length;
  
  const progressPercentage = totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0;
  
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-gray-700">Progression Exacte</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-900">
            {completedCount}/{totalTasks}
          </span>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        </div>
      </div>
      
      {/* Barre de progression principale */}
      <div className="w-full bg-gray-200 rounded-full h-3 mb-3 relative overflow-hidden">
        <div 
          className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all duration-700 relative"
          style={{ width: `${progressPercentage}%` }}
        >
          {progressPercentage > 0 && (
            <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full"></div>
          )}
        </div>
        {/* Pourcentage sur la barre */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold text-gray-700">
            {Math.round(progressPercentage)}%
          </span>
        </div>
      </div>
      
      {/* Message d'état */}
      <div className="mt-3 text-center">
        {pendingCount === 0 ? (
          <p className="text-green-600 text-sm font-bold">
            🎉 Toutes les tâches sont terminées !
          </p>
        ) : (
          <p className="text-gray-600 text-sm">
            Encore <span className="font-bold text-orange-600">{pendingCount}</span> tâche{pendingCount > 1 ? 's' : ''} à traiter
          </p>
        )}
      </div>
    </div>
  );
};

// ✅ COMPOSANT PRINCIPAL avec toutes les fonctions
const BeautifulAuditInterface: React.FC<AuditorInterfaceProps> = ({ 
 onBackToDashboard, 
 onSessionStateChange 
}) => {
 const [tasks, setTasks] = useState<ParsedTask[]>([]);
 const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
 const [auditResults, setAuditResults] = useState<AuditResult[]>([]);
 const [loading, setLoading] = useState(false);
 const [loadingAgents, setLoadingAgents] = useState(false);
 const [savingToAppwrite, setSavingToAppwrite] = useState(false);
 const [taskType, setTaskType] = useState<'pair_only' | 'complete_match'>('pair_only');
 const [agents, setAgents] = useState<Agent[]>([]);
 const [selectedAgent, setSelectedAgent] = useState('');
 const [agentSearch, setAgentSearch] = useState('');
 const [date, setDate] = useState('2025-07-29');
 const [sessionStarted, setSessionStarted] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'disconnected'>('unknown');

 // États Appwrite
 const [currentSession, setCurrentSession] = useState<AuditSession | null>(null);
 const [currentUser, setCurrentUser] = useState<any>(null);
 const [appwriteStatus, setAppwriteStatus] = useState<'checking' | 'connected' | 'error'>('checking');
 const [pendingSession, setPendingSession] = useState<any>(null);
 const [checkingPendingSession, setCheckingPendingSession] = useState(true);


// Helpers statut
const isDone = (t: ParsedTask | undefined): boolean => {
  return !!t && (t.audit_status === 'approved' || t.audit_status === 'rejected');
};

const isPending = (t: ParsedTask | undefined): boolean => {
  return !!t && (!t.audit_status || t.audit_status === 'pending');
};

const firstPendingIndex = (list: ParsedTask[]): number => {
  return list.findIndex(t => isPending(t));
};

const nextPendingIndex = (from: number, list: ParsedTask[]): number => {
  // Chercher à partir de l'index 'from' (inclus)
  for (let i = from; i < list.length; i++) {
    if (isPending(list[i])) {
      return i;
    }
  }
  return -1; // Aucune tâche pending trouvée
};

const countPendingTasks = (list: ParsedTask[]): number => {
  return list.filter(t => isPending(t)).length;
};

const countCompletedTasks = (list: ParsedTask[]): number => {
  return list.filter(t => isDone(t)).length;
};


 // ✅ useEffect pour initialiser l'app avec Appwrite
 useEffect(() => {
   const initializeApp = async () => {
     try {
       // 1. D'abord charger les agents
       console.log('🔍 Chargement agents depuis API...');
       const agentsList = focalApiService.getAllAvailableAgents();
       setAgents(agentsList);
       if (agentsList.length > 0) {
         setSelectedAgent(agentsList[0].email);
       }
       setConnectionStatus('connected');
       console.log('✅ Agents chargés:', agentsList.length);

       // 2. Puis vérifier l'auth Appwrite
       const user = await authService.getCurrentUser();
       if (user) {
         setCurrentUser(user);
         setAppwriteStatus('connected');
         console.log('✅ Utilisateur connecté:', user.profile.email);

         // 3. Enfin vérifier session en attente
         await checkForPendingSession(user);
       } else {
         setAppwriteStatus('error');
         setError('Veuillez vous connecter à Appwrite');
         setCheckingPendingSession(false);
       }
     } catch (error: any) {
       console.error('❌ Erreur initialisation:', error);
       setAppwriteStatus('error');
       setError('Erreur de connexion');
       setCheckingPendingSession(false);
       setConnectionStatus('disconnected');
     } finally {
       setLoadingAgents(false);
     }
   };

   setLoadingAgents(true);
   initializeApp();
 }, []);

 // ✅ Vérification session en attente avec Appwrite
 const checkForPendingSession = async (user: any) => {
   setCheckingPendingSession(true);
   try {
     console.log('🔍 Vérification session pour:', user.profile.email);
     console.log('🆔 User ID:', user.profile.$id);

     // ✅ APPWRITE - Voir toutes les sessions utilisateur
     const allUserSessions = await databases.listDocuments(
       appwriteDatabaseId,
       'audit_sessions',
       [
         Query.equal('auditor_id', user.profile.$id),
         Query.orderDesc('$createdAt'),
         Query.limit(1000)
       ]
     );

     console.log('📋 TOUTES les sessions utilisateur avec DÉTAILS:');
     allUserSessions.documents?.forEach((session, index) => {
       console.log(`  Session ${index + 1}:`, {
         id: session.$id,
         session_status: session.session_status,
         agent_email: session.agent_email,
         audit_date: session.audit_date,
         completed_tasks: session.completed_tasks,
         total_tasks: session.total_tasks,
         created_at: session.$createdAt
       });
     });

     // ✅ APPWRITE - Chercher sessions actives (in_progress ou paused)
     const activeSessionsInProgress = await databases.listDocuments(
       appwriteDatabaseId,
       'audit_sessions',
       [
         Query.equal('auditor_id', user.profile.$id),
         Query.equal('session_status', 'in_progress'),
         Query.orderDesc('$createdAt'),
         Query.limit(1)
       ]
     );

     const activeSessionsPaused = await databases.listDocuments(
       appwriteDatabaseId,
       'audit_sessions',
       [
         Query.equal('auditor_id', user.profile.$id),
         Query.equal('session_status', 'paused'),
         Query.orderDesc('$createdAt'),
         Query.limit(1)
       ]
     );

     const activeSessions = [
       ...activeSessionsInProgress.documents,
       ...activeSessionsPaused.documents
     ].sort((a, b) => new Date(b.$createdAt).getTime() - new Date(a.$createdAt).getTime());

     console.log('🔍 Sessions actives (in_progress ou paused):', activeSessions);

     if (activeSessions && activeSessions.length > 0) {
       const session = activeSessions[0];
       console.log('🔄 Session active trouvée:', session);

       // ✅ APPWRITE - Forcer le statut à in_progress si c'est paused
       if (session.session_status === 'paused') {
         console.log('⏸️ Session en pause, remise en cours...');
         try {
           await databases.updateDocument(
             appwriteDatabaseId,
             'audit_sessions',
             session.$id,
             { 
               session_status: 'in_progress'
             }
           );
           session.session_status = 'in_progress';
           console.log('✅ Session remise en in_progress');
         } catch (updateError) {
           console.warn('⚠️ Erreur mise à jour statut:', updateError);
         }
       }

       setPendingSession(session);
       setSelectedAgent(session.agent_email);
       setDate(session.audit_date);
       if (session.session_type) {
         setTaskType(session.session_type);
       }
     } else {
       console.log('✅ Aucune session active trouvée');
       setPendingSession(null);
     }

   } catch (error: any) {
     console.error('❌ Erreur vérification session:', error);
     setPendingSession(null);
   } finally {
     setCheckingPendingSession(false);
   }
 };

 // Filtrer les agents selon la recherche
 const filteredAgents = agents.filter(agent =>
   agent.email.toLowerCase().includes(agentSearch.toLowerCase()) ||
   agent.displayName.toLowerCase().includes(agentSearch.toLowerCase()) ||
   agent.userId.includes(agentSearch)
 );

 // Test de connexion API
 const testConnection = async () => {
   setLoading(true);
   try {
     console.log('🔍 Test connexion API...');
     const isConnected = await focalApiService.testConnection();
     setConnectionStatus(isConnected ? 'connected' : 'disconnected');
     setError(isConnected ? null : 'Connexion échouée - Vérifiez le serveur proxy localhost:3001');
     console.log('📡 Résultat test:', isConnected ? 'Connecté' : 'Échec');
   } catch (error: any) {
     setConnectionStatus('disconnected');
     setError(`Erreur de connexion: ${error.message}`);
     console.error('💥 Erreur test connexion:', error);
   } finally {
     setLoading(false);
   }
 };

 // ✅ Créer une session d'audit dans Appwrite
 const createAuditSession = async (agentEmail: string, auditDate: string, totalTasks: number): Promise<AuditSession> => {
   try {
     console.log('📝 Création session Appwrite...');

     const session = await auditSessionService.createSession(
       currentUser.profile.$id,
       agentEmail,
       auditDate
     );

     // ✅ Ajouter le type de session pour la reprise
     const updatedSession = await databases.updateDocument(
       appwriteDatabaseId,
       'audit_sessions',
       session.$id,
       {
         session_type: taskType,
         total_tasks: totalTasks,
         completed_tasks: 0,
         approved_tasks: 0,
         rejected_tasks: 0
       }
     );

     console.log('✅ Session créée:', updatedSession.$id);
     return updatedSession as any;
   } catch (error) {
     console.error('❌ Erreur création session:', error);
     throw error;
   }
 };

 // ✅ Sauvegarder les tâches dans Appwrite
 const saveTasksToAppwrite = async (session: AuditSession, tasks: ParsedTask[]): Promise<ParsedTask[]> => {
   try {
     console.log('💾 Sauvegarde tâches dans Appwrite...');

     const tasksWithAppwriteIds = await Promise.all(
       tasks.map(async (task, index) => {
         try {
           console.log(`📋 Traitement tâche ${index + 1}/${tasks.length}: ${task.match_task_id}`);

           // ✅ VÉRIFIER SI LA TÂCHE EXISTE DÉJÀ
           const existingTasks = await databases.listDocuments(
             appwriteDatabaseId,
             'audit_tasks',
             [
               Query.equal('match_task_id', task.match_task_id),
               Query.equal('session_id', session.$id!),
               Query.limit(1)
             ]
           );

           // Si la tâche existe déjà, la retourner avec son ID
           if (existingTasks.documents.length > 0) {
             const existingTask = existingTasks.documents[0];
             console.log(`📋 Tâche ${task.match_task_id} déjà existante, récupération...`);
             return {
               ...task,
               appwrite_task_id: existingTask.$id,
               audit_status: existingTask.audit_status as 'pending' | 'approved' | 'rejected'
             };
           }

           // ✅ TRAITEMENT ET TRONCATURE DES URLs
           const originalUrls = {
             template: task.template_image_urls[0] || '',
             plano: task.plano_image_urls[0] || '',
             model: task.model_drawn_template_urls[0] || '',
             agent: task.agent_submitted_urls[0] || ''
           };

           console.log(`🔗 URLs originales pour tâche ${task.match_task_id}:`, {
             template_length: originalUrls.template.length,
             plano_length: originalUrls.plano.length,
             model_length: originalUrls.model.length,
             agent_length: originalUrls.agent.length
           });

           // Tronquer TOUTES les URLs
           const truncatedUrls = {
             template_image_url: truncateUrl(originalUrls.template),
             plano_image_url: truncateUrl(originalUrls.plano),
             model_template_url: truncateUrl(originalUrls.model),
             agent_submitted_url: truncateUrl(originalUrls.agent)
           };

           console.log(`🔗 URLs après troncature:`, {
             template_length: truncatedUrls.template_image_url.length,
             plano_length: truncatedUrls.plano_image_url.length,
             model_length: truncatedUrls.model_template_url.length,
             agent_length: truncatedUrls.agent_submitted_url.length
           });

           // ✅ VALIDATION - Vérifier qu'aucune URL ne dépasse 3000 chars
           Object.entries(truncatedUrls).forEach(([key, url]) => {
             if (url && url.length > 3000) {
               console.error(`❌ URL ${key} encore trop longue après troncature: ${url.length} chars`);
               throw new Error(`URL ${key} trop longue: ${url.length} chars`);
             }
           });

           // ✅ DONNÉES COMPLÈTES POUR APPWRITE
           const taskData = {
             session_id: session.$id!,
             match_task_id: task.match_task_id,
             agent_email: task.email || selectedAgent,
             task_date: task.date,
             task_state: task.state,
             priority_names: (task.priority_names || '').substring(0, 1000),
             issue_names: (task.issue_names || '').substring(0, 1000),
             processing_time_seconds: task.processing_time_seconds || 0,
             template_unpaired: task.template_unpaired || 0,
             template_total: task.template_total || 0,
             plano_unpaired: task.plano_unpaired || 0,
             plano_total: task.plano_total || 0,

             // ✅ URLs TRONQUÉES
             ...truncatedUrls,

             // ✅ MÉTADONNÉES
             task_type: task.task_type,
             audit_status: 'pending'
           };

           console.log(`💾 Création document pour tâche ${task.match_task_id}...`);

           // ✅ CRÉER LA TÂCHE
           const newTask = await databases.createDocument(
             appwriteDatabaseId,
             'audit_tasks',
             ID.unique(),
             taskData
           );

           console.log(`✅ Tâche ${task.match_task_id} sauvegardée avec ID: ${newTask.$id}`);

           return {
             ...task,
             appwrite_task_id: newTask.$id,
             audit_status: 'pending' as const
           };

         } catch (error) {
           console.error(`❌ Erreur sauvegarde tâche ${task.match_task_id}:`, error);
           return task; // Retourner la tâche sans ID Appwrite en cas d'erreur
         }
       })
     );

     const successCount = tasksWithAppwriteIds.filter(t => t.appwrite_task_id).length;
     console.log(`✅ Sauvegarde terminée: ${successCount}/${tasks.length} tâches sauvegardées`);
     
     return tasksWithAppwriteIds;
   } catch (error) {
     console.error('❌ Erreur globale sauvegarde tâches:', error);
     throw error;
   }
 };

 // ✅ Démarrer session avec Appwrite ET PARSING AMÉLIORÉ
 const startAuditSession = async () => {
   if (appwriteStatus !== 'connected') {
     setError('Connexion Appwrite requise');
     return;
   }

   setLoading(true);
   setError(null);

   try {
     if (pendingSession) {
       // ✅ REPRENDRE SESSION EXISTANTE
       console.log('🔄 Reprise session en attente...');
       await resumeExistingSession(pendingSession);
     } else {
       // ✅ CRÉER NOUVELLE SESSION
       if (!selectedAgent) {
         setError('Veuillez sélectionner un agent');
         setLoading(false);
         return;
       }

       console.log('🚀 Création nouvelle session...');

       let rawTasks: any[] = [];

       if (taskType === 'pair_only') {
         console.log('📡 Récupération Pair Only Tasks...');
         rawTasks = await focalApiService.getPairOnlyTasks({
           dateFrom: date,
           dateTo: date,
           userEmail: selectedAgent
         });
       } else {
         console.log('📡 Récupération Complete Match Tasks...');
         rawTasks = await focalApiService.getCompleteMatchTasks({
           dateFrom: date,
           dateTo: date,
           userEmail: selectedAgent
         });
       }

       console.log('📊 Données brutes récupérées depuis API:', rawTasks.length);

       if (rawTasks.length === 0) {
         throw new Error(`Aucune tâche ${taskType} trouvée pour ${selectedAgent} le ${date}. Essayez une autre date ou un autre agent.`);
       }

       // ✅ UTILISER LE NOUVEAU PARSING avec debug complet
       const parsedTasks = parseAuditDataWithFixedExtraction(rawTasks, taskType);
       console.log('🔧 Tâches parsées avec debug:', parsedTasks.length);

       // Créer la session dans Appwrite
       const session = await createAuditSession(selectedAgent, date, parsedTasks.length);
       setCurrentSession(session);
       setPendingSession(session);

       // Sauvegarder les tâches dans Appwrite
       const tasksWithAppwriteIds = await saveTasksToAppwrite(session, parsedTasks);

       setTasks(tasksWithAppwriteIds);
       setSessionStarted(true);
       setCurrentTaskIndex(0);
       setAuditResults([]);

       // ✅ NOTIFIER que la session est active
       onSessionStateChange?.(true);

       // ✅ Sauvegarder dans localStorage
       localStorage.setItem('audit_view_state', JSON.stringify({
         currentView: 'audit',
         sessionActive: true,
         timestamp: new Date().toISOString()
       }));
     }
   } catch (error: any) {
     console.error('💥 Erreur session:', error);
     setError(error.message);
   } finally {
     setLoading(false);
   }
 };

 // ✅ Sauvegarder la progression avec Appwrite - VERSION OPTIMISÉE
 const saveProgress = async (forcedIndex?: number, forcedTasks?: ParsedTask[]) => {
  if (currentSession && currentUser) {
    try {
      const idx = (typeof forcedIndex === 'number') ? forcedIndex : currentTaskIndex;
      const tlist = forcedTasks ?? tasks;

      const minimalTasksData = tlist.map(t => ({
        match_task_id: t.match_task_id,
        appwrite_task_id: t.appwrite_task_id,
        audit_status: t.audit_status,
        task_type: t.task_type
      }));

      const tasksDataString = JSON.stringify(minimalTasksData);

      if (tasksDataString.length > 9500) {
        console.warn(`⚠️ Données trop volumineuses (${tasksDataString.length} chars), troncature...`);
      }

      const completedCount = tlist.filter(isDone).length;

      const progressData = {
        session_id: currentSession.$id,
        auditor_id: currentUser.profile.$id,
        current_task_index: idx,
        total_tasks: tlist.length,
        completed_tasks: completedCount,
        tasks_data: tasksDataString.length <= 9500
          ? tasksDataString
          : JSON.stringify(minimalTasksData.slice(0, 30)),
        last_updated: new Date().toISOString()
      };

      const existingProgress = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_progress',
        [
          Query.equal('session_id', currentSession.$id!),
          Query.equal('auditor_id', currentUser.profile.$id),
          Query.limit(10)
        ]
      );

      if (existingProgress.documents.length > 0) {
        await databases.updateDocument(
          appwriteDatabaseId,
          'audit_progress',
          existingProgress.documents[0].$id,
          progressData
        );
        console.log('✅ Progression mise à jour');
      } else {
        await databases.createDocument(
          appwriteDatabaseId,
          'audit_progress',
          ID.unique(),
          progressData
        );
        console.log('✅ Progression créée');
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde progression:', error);
    }
  }
};

 // ✅ Reprendre une session existante avec Appwrite ET EXTRACTION FIXÉE
 const resumeExistingSession = async (session: any) => {
  try {
    console.log('🔄 Reprise session existante:', session.$id);

    // Charger les tâches de la session (sauvegardées)
    const sessionTasks = await databases.listDocuments(
      appwriteDatabaseId,
      'audit_tasks',
      [
        Query.equal('session_id', session.$id),
        Query.orderAsc('$createdAt'),
        Query.limit(1000)
      ]
    );

    console.log(`📊 ${sessionTasks.documents.length} tâches trouvées en base`);

    // Re-récupérer les données fraîches depuis l'API (URLs complètes)
    console.log('📡 Re-récupération données fraîches depuis API...');
    let freshTasks: any[] = [];
    try {
      if (session.session_type === 'pair_only') {
        freshTasks = await focalApiService.getPairOnlyTasks({
          dateFrom: session.audit_date,
          dateTo: session.audit_date,
          userEmail: session.agent_email
        });
      } else {
        freshTasks = await focalApiService.getCompleteMatchTasks({
          dateFrom: session.audit_date,
          dateTo: session.audit_date,
          userEmail: session.agent_email
        });
      }
      console.log(`📊 ${freshTasks.length} tâches fraîches récupérées depuis API`);
    } catch (apiError) {
      console.warn('⚠️ Impossible de récupérer les données fraîches, utilisation des données sauvegardées');
      freshTasks = [];
    }

    // Reconstruction: merger des données fraîches et sauvegardées
    const reconstructedTasks: ParsedTask[] = sessionTasks.documents.map((dbTask: any) => {
      const freshTask = freshTasks.find((ft: any) => ft.match_task_id === dbTask.match_task_id);

      let planoImageUrls: string[] = [];
      let templateImageUrls: string[] = [];
      let modelDrawnTemplateUrls: string[] = [];
      let agentSubmittedUrls: string[] = [];

      if (freshTask) {
        // Utiliser les données fraîches (URLs complètes)
        console.log(`🔄 Utilisation données fraîches pour tâche ${dbTask.match_task_id}`);

        if (session.session_type === 'pair_only') {
          agentSubmittedUrls = extractImageUrlsFixed(freshTask.template_image);
          modelDrawnTemplateUrls = [freshTask.model_drawn_template].filter(Boolean);
          templateImageUrls = [];
        } else {
          templateImageUrls = extractImageUrlsFixed(freshTask.template_image);
          agentSubmittedUrls = [];
          modelDrawnTemplateUrls = [];
        }

        planoImageUrls = extractImageUrlsFixed(freshTask.plano_image);

        console.log(`🖼️ URLs fraîches pour tâche ${dbTask.match_task_id}:`, {
          plano: planoImageUrls.length,
          template: templateImageUrls.length,
          agent: agentSubmittedUrls.length,
          model: modelDrawnTemplateUrls.length
        });

      } else {
        // Fallback sur les données sauvegardées
        console.log(`💾 Utilisation données sauvegardées pour tâche ${dbTask.match_task_id}`);

        templateImageUrls = dbTask.template_image_url ? [dbTask.template_image_url] : [];
        planoImageUrls = dbTask.plano_image_url ? [dbTask.plano_image_url] : [];
        modelDrawnTemplateUrls = dbTask.model_template_url ? [dbTask.model_template_url] : [];
        agentSubmittedUrls = dbTask.agent_submitted_url ? [dbTask.agent_submitted_url] : [];
      }

      return {
        match_task_id: dbTask.match_task_id,
        date: dbTask.task_date,
        state: dbTask.task_state,
        email: dbTask.agent_email,
        priority_names: dbTask.priority_names,
        issue_names: dbTask.issue_names,
        processing_time_seconds: dbTask.processing_time_seconds,
        template_unpaired: dbTask.template_unpaired,
        template_total: dbTask.template_total,
        plano_unpaired: dbTask.plano_unpaired,
        plano_total: dbTask.plano_total,

        // URLs complètes (fraîches ou sauvegardées)
        template_image_urls: templateImageUrls,
        plano_image_urls: planoImageUrls,
        model_drawn_template_urls: modelDrawnTemplateUrls,
        agent_submitted_urls: agentSubmittedUrls,

        model_template_used: dbTask.model_template_used || 0,
        unlock_draw_mode_used: dbTask.unlock_draw_mode_used || 0,
        task_type: (dbTask.task_type || session.session_type || 'complete_match') as 'pair_only' | 'complete_match',
        appwrite_task_id: dbTask.$id,
        audit_status: dbTask.audit_status,
        audit_comment: dbTask.audit_comment
      };
    });

    console.log('🔍 Tasks reconstruites - première tâche plano:', reconstructedTasks[0]?.plano_image_urls.length);

    // Charger la progression sauvegardée
    const progressData = await databases.listDocuments(
      appwriteDatabaseId,
      'audit_progress',
      [
        Query.equal('session_id', session.$id),
        Query.equal('auditor_id', currentUser.profile.$id),
        Query.limit(10)
      ]
    );

    const savedIndex: number = progressData.documents[0]?.current_task_index ?? 0;

    // ✅ CORRECTION MAJEURE: Compter les tâches RÉELLEMENT pending
    const pendingTasks = reconstructedTasks.filter(t => isPending(t));
    const pendingCount = pendingTasks.length;
    const completedCount = countCompletedTasks(reconstructedTasks);
    
    console.log(`📊 État session à reprendre:`, {
      totalTasks: reconstructedTasks.length,
      completed: completedCount,
      pending: pendingCount,
      savedIndex,
      firstPendingTask: pendingTasks[0]?.match_task_id || 'aucune'
    });

    // ✅ Si AUCUNE tâche pending, la session est VRAIMENT terminée
    if (pendingCount === 0) {
      const validatedCount = reconstructedTasks.filter(t => t.audit_status === 'approved').length;
      const commentedCount = reconstructedTasks.filter(t => t.audit_status === 'rejected').length;

      alert(
        `🎉 Session déjà entièrement terminée !\n\n` +
        `✅ ${validatedCount} validées\n` +
        `💬 ${commentedCount} commentées\n` +
        `📊 Total: ${reconstructedTasks.length} tâches\n\n` +
        `Toutes les tâches ont été auditées avec succès !`
      );
      
      await completeSession(session.$id);
      return;
    }

    // ✅ CORRECTION: Trouver la PREMIÈRE tâche pending (pas à partir de savedIndex)
    const resumeIndex = firstPendingIndex(reconstructedTasks);
    
    if (resumeIndex === -1) {
      console.error('❌ ANOMALIE: pendingCount > 0 mais firstPendingIndex = -1');
      alert('Erreur de cohérence des données. Session marquée comme terminée.');
      await completeSession(session.$id);
      return;
    }

    console.log(`🎯 Reprise à la première tâche pending: index ${resumeIndex + 1}/${reconstructedTasks.length} (tâche ID: ${reconstructedTasks[resumeIndex].match_task_id})`);

    // ✅ Reprendre la session sur la première tâche pending
    setCurrentSession(session);
    setTasks(reconstructedTasks);
    setCurrentTaskIndex(resumeIndex);
    setSessionStarted(true);
    setAuditResults([]);

    onSessionStateChange?.(true);

    localStorage.setItem('audit_view_state', JSON.stringify({
      currentView: 'audit',
      sessionActive: true,
      timestamp: new Date().toISOString()
    }));

    // ✅ Sauvegarder la progression avec l'index correct
    await saveProgress(resumeIndex, reconstructedTasks);

    console.log(`✅ Session reprise avec succès:`);
    console.log(`📊 État: ${completedCount}/${reconstructedTasks.length} terminées, ${pendingCount} restantes`);
    console.log(`🎯 Position: Tâche ${resumeIndex + 1} (ID: ${reconstructedTasks[resumeIndex].match_task_id})`);

  } catch (error) {
    console.error('❌ Erreur reprise session:', error);
    setError('Impossible de reprendre la session');
  }
};

 // ✅ Compléter une session avec Appwrite
 const completeSession = async (sessionId: string) => {
   try {
     await databases.updateDocument(
       appwriteDatabaseId,
       'audit_sessions',
       sessionId,
       {
         session_status: 'completed'
       }
     );

     setPendingSession(null);
     console.log('✅ Session terminée et supprimée de pending');
   } catch (error) {
     console.error('❌ Erreur finalisation session:', error);
   }
 };

 // ✅ Mettre à jour une tâche dans Appwrite
 const updateTaskInAppwrite = async (
  task: ParsedTask,
  status: 'approved' | 'rejected',
  comment?: string,
  severity?: string
) => {
  if (!currentSession) return;

  try {
    // 1) Màj du document "audit_tasks"
    await databases.updateDocument(
      appwriteDatabaseId,
      'audit_tasks',
      task.appwrite_task_id,
      {
        audit_status: status,
        audit_comment: comment ?? task.audit_comment ?? null,
        audit_severity: severity ?? null,
        audited_at: new Date().toISOString(),
        auditor_id: currentUser?.profile?.$id ?? null,
        auditor_email: currentUser?.profile?.email ?? null,
      }
    );

    // 2) Anti double-comptage des stats de session
    //    On compare l'ancien statut (task.audit_status) et le nouveau "status"
    const prev = task.audit_status; // 'pending' | 'approved' | 'rejected'

    // Incréments calculés intelligemment
    const incCompleted = (prev !== 'approved' && prev !== 'rejected') ? 1 : 0;
    const incApproved  = (prev !== 'approved' && status === 'approved') ? 1 : 0;
    const incRejected  = (prev !== 'rejected' && status === 'rejected') ? 1 : 0;

    if (incCompleted || incApproved || incRejected) {
      const updatedStats = {
        completed_tasks: (currentSession.completed_tasks || 0) + incCompleted,
        approved_tasks:  (currentSession.approved_tasks  || 0) + incApproved,
        rejected_tasks:  (currentSession.rejected_tasks  || 0) + incRejected,
        last_updated: new Date().toISOString(),
      };

      await databases.updateDocument(
        appwriteDatabaseId,
        'audit_sessions',
        currentSession.$id!,
        updatedStats
      );

      setCurrentSession({ ...currentSession, ...updatedStats });
      console.log('📊 Stats session mises à jour (sans double comptage)');
    } else {
      console.log('ℹ️ Aucune mise à jour des stats (tâche déjà comptabilisée).');
    }

  } catch (err) {
    console.error('❌ Erreur updateTaskInAppwrite:', err);
    throw err;
  }
};

 // ✅ handleValidate avec Appwrite
 const handleValidate = async (): Promise<void> => {
  const currentTask = tasks[currentTaskIndex];
  if (!currentTask) {
    console.warn('❌ handleValidate: Aucune tâche courante');
    return;
  }

  console.log(`🔍 handleValidate: Validation tâche ${currentTaskIndex + 1}/${tasks.length} (ID: ${currentTask.match_task_id})`);

  try {
    setSavingToAppwrite(true);

    // 1) MAJ Appwrite
    await updateTaskInAppwrite(currentTask, 'approved');

    // 2) MAJ locale - FORCER le nouveau statut
    const updatedTasks: ParsedTask[] = tasks.map((t, i) =>
      i === currentTaskIndex
        ? { ...t, audit_status: 'approved' as const }
        : t
    );
    setTasks(updatedTasks);

    // 3) Journal local
    const result: AuditResult = {
      taskId: currentTask.match_task_id,
      appwriteTaskId: currentTask.appwrite_task_id,
      taskType: currentTask.task_type,
      result: 'validated',
      timestamp: new Date().toISOString(),
      auditorEmail: currentUser?.profile?.email || 'unknown',
    };
    setAuditResults(prev => [...prev, result]);

    // 4) ✅ DÉCOMPTE EXACT avec logging détaillé
    const pendingCount = countPendingTasks(updatedTasks);
    const completedCount = countCompletedTasks(updatedTasks);
    
    console.log(`📊 État après validation:`, {
      totalTasks: updatedTasks.length,
      completed: completedCount,
      pending: pendingCount,
      currentIndex: currentTaskIndex
    });

    // 5) ✅ LOGIQUE DE NAVIGATION CORRIGÉE
    const nextIdx = nextPendingIndex(currentTaskIndex + 1, updatedTasks);
    
    console.log(`🔍 Recherche prochaine tâche: nextIdx=${nextIdx}`);

    // 6) Sauvegarder progression
    await saveProgress(nextIdx === -1 ? updatedTasks.length - 1 : nextIdx, updatedTasks);

    // 7) ✅ VÉRIFICATION STRICTE DE FIN
    if (pendingCount === 0) {
      console.log(`🎯 TOUTES LES TÂCHES TERMINÉES! Completed: ${completedCount}/${updatedTasks.length}`);
      
      // Petite pause pour que l'utilisateur voie la dernière validation
      setTimeout(() => {
        finishSession();
      }, 1000);
    } else if (nextIdx !== -1) {
      console.log(`➡️ Navigation vers tâche ${nextIdx + 1} (${pendingCount} restantes)`);
      
      setTimeout(() => {
        setCurrentTaskIndex(nextIdx);
      }, 300);
    } else {
      // ✅ CAS IMPOSSIBLE - Sécurité
      console.error(`⚠️ ANOMALIE: ${pendingCount} tâches pending mais nextIdx=-1`);
      
      // Chercher la première tâche pending dans toute la liste
      const fallbackIdx = firstPendingIndex(updatedTasks);
      if (fallbackIdx !== -1) {
        console.log(`🔧 Fallback: Navigation vers tâche ${fallbackIdx + 1}`);
        setCurrentTaskIndex(fallbackIdx);
      } else {
        console.log(`🏁 Aucune tâche pending trouvée, session terminée`);
        finishSession();
      }
    }

  } catch (err) {
    console.error('❌ handleValidate error:', err);
    alert('Erreur lors de la validation. Veuillez réessayer.');
  } finally {
    setSavingToAppwrite(false);
  }
};

 // ✅ handleComment avec Appwrite
 const handleComment = async (
  comment: string,
  severity?: 'minor' | 'major' | 'critical'
): Promise<void> => {
  const currentTask = tasks[currentTaskIndex];
  if (!currentTask) {
    console.warn('❌ handleComment: Aucune tâche courante');
    return;
  }

  console.log(`🔍 handleComment: Commentaire tâche ${currentTaskIndex + 1}/${tasks.length} (ID: ${currentTask.match_task_id})`);

  try {
    setSavingToAppwrite(true);

    // 1) MAJ Appwrite
    await updateTaskInAppwrite(currentTask, 'rejected', comment, severity);

    // 2) MAJ locale
    const updatedTasks: ParsedTask[] = tasks.map((t, i) =>
      i === currentTaskIndex
        ? {
            ...t,
            audit_status: 'rejected' as const,
            audit_comment: comment
          }
        : t
    );
    setTasks(updatedTasks);

    // 3) Journal local
    const result: AuditResult = {
      taskId: currentTask.match_task_id,
      appwriteTaskId: currentTask.appwrite_task_id,
      taskType: currentTask.task_type,
      result: 'commented',
      comment,
      severity,
      timestamp: new Date().toISOString(),
      auditorEmail: currentUser?.profile?.email || 'unknown'
    };
    setAuditResults(prev => [...prev, result]);

    // 4) ✅ DÉCOMPTE EXACT
    const pendingCount = countPendingTasks(updatedTasks);
    const completedCount = countCompletedTasks(updatedTasks);
    
    console.log(`📊 État après commentaire:`, {
      totalTasks: updatedTasks.length,
      completed: completedCount,
      pending: pendingCount,
      currentIndex: currentTaskIndex
    });

    // 5) ✅ NAVIGATION CORRIGÉE
    const nextIdx = nextPendingIndex(currentTaskIndex + 1, updatedTasks);
    
    console.log(`🔍 Recherche prochaine tâche: nextIdx=${nextIdx}`);

    // 6) Sauvegarder progression
    await saveProgress(nextIdx === -1 ? updatedTasks.length - 1 : nextIdx, updatedTasks);

    // 7) ✅ VÉRIFICATION STRICTE DE FIN
    if (pendingCount === 0) {
      console.log(`🎯 TOUTES LES TÂCHES TERMINÉES! Completed: ${completedCount}/${updatedTasks.length}`);
      
      setTimeout(() => {
        finishSession();
      }, 1000);
    } else if (nextIdx !== -1) {
      console.log(`➡️ Navigation vers tâche ${nextIdx + 1} (${pendingCount} restantes)`);
      
      setTimeout(() => {
        setCurrentTaskIndex(nextIdx);
      }, 300);
    } else {
      // ✅ CAS IMPOSSIBLE - Sécurité
      console.error(`⚠️ ANOMALIE: ${pendingCount} tâches pending mais nextIdx=-1`);
      
      const fallbackIdx = firstPendingIndex(updatedTasks);
      if (fallbackIdx !== -1) {
        console.log(`🔧 Fallback: Navigation vers tâche ${fallbackIdx + 1}`);
        setCurrentTaskIndex(fallbackIdx);
      } else {
        console.log(`🏁 Aucune tâche pending trouvée, session terminée`);
        finishSession();
      }
    }

  } catch (err) {
    console.error('❌ handleComment error:', err);
    alert('Erreur lors du commentaire. Veuillez réessayer.');
  } finally {
    setSavingToAppwrite(false);
  }
};

 // ✅ Finaliser la session avec Appwrite
 const finishSession = async () => {
  console.log('🏁 === FINALISATION SESSION ===');
  
  try {
    // ✅ VÉRIFICATION FINALE STRICTE
    const finalPendingCount = countPendingTasks(tasks);
    const finalCompletedCount = countCompletedTasks(tasks);
    
    console.log(`📊 État final session:`, {
      totalTasks: tasks.length,
      completed: finalCompletedCount,
      pending: finalPendingCount,
      allDone: finalPendingCount === 0
    });

    // ✅ SÉCURITÉ: Vérifier qu'il n'y a vraiment plus de tâches
    if (finalPendingCount > 0) {
      console.warn(`⚠️ ATTENTION: ${finalPendingCount} tâches encore pending!`);
      
      const shouldContinue = window.confirm(
        `⚠️ Il reste ${finalPendingCount} tâche(s) en attente.\n\n` +
        `Voulez-vous:\n` +
        `• OUI: Continuer l'audit des tâches restantes\n` +
        `• NON: Forcer la fin de session (non recommandé)`
      );

      if (shouldContinue) {
        // Reprendre à la première tâche pending
        const resumeIdx = firstPendingIndex(tasks);
        if (resumeIdx !== -1) {
          console.log(`🔄 Reprise forcée à l'index ${resumeIdx}`);
          setCurrentTaskIndex(resumeIdx);
          return; // Ne pas finir la session
        }
      }
    }

    // ✅ Marquer comme completed dans Appwrite
    if (currentSession) {
      await completeSession(currentSession.$id!);
    }

    // ✅ Calculer les vrais totaux
    const validatedCount = tasks.filter(t => t.audit_status === 'approved').length;
    const commentedCount = tasks.filter(t => t.audit_status === 'rejected').length;
    const totalProcessed = validatedCount + commentedCount;

    console.log(`✅ Session terminée:`, {
      validated: validatedCount,
      commented: commentedCount,
      totalProcessed,
      totalTasks: tasks.length,
      completion: `${totalProcessed}/${tasks.length}`
    });

    alert(
      `🎉 Session d'audit terminée !\n\n` +
      `📊 Résultats:\n` +
      `✅ Validées: ${validatedCount}\n` +
      `💬 Commentées: ${commentedCount}\n` +
      `📈 Total traité: ${totalProcessed}/${tasks.length} tâches\n\n` +
      `Retour au dashboard dans 3 secondes...`
    );
    
    // ✅ Nettoyage des états
    setSessionStarted(false);
    setCurrentTaskIndex(0);
    setAuditResults([]);
    setTasks([]);
    setCurrentSession(null);
    setPendingSession(null);

    onSessionStateChange?.(false);
    localStorage.removeItem('audit_view_state');
    
    // Retour au dashboard
    setTimeout(() => {
      if (onBackToDashboard) {
        onBackToDashboard();
      }
    }, 3000);
     
  } catch (error) {
    console.error('❌ Erreur finalisation session:', error);
    alert('Erreur lors de la finalisation. Session marquée comme terminée.');
  }
};

 // ✅ handleLogout avec Appwrite
 const handleLogout = async () => {
   if (window.confirm('Quitter la session d\'audit ? Les données non sauvegardées seront perdues.')) {
     try {
       if (currentSession) {
         await databases.updateDocument(
           appwriteDatabaseId,
           'audit_sessions',
           currentSession.$id!,
           {
             session_status: 'paused'
           }
         );
         console.log('⏸️ Session en pause');
       }
     } catch (error) {
       console.error('❌ Erreur pause session:', error);
     }
     
     setSessionStarted(false);
     setTasks([]);
     setCurrentTaskIndex(0);
     setAuditResults([]);
     setCurrentSession(null);

     onSessionStateChange?.(false);
     localStorage.removeItem('audit_view_state');

     if (onBackToDashboard) {
       onBackToDashboard();
     }
   }
 };

 // Composant Modal Loader Magnifique
 const LoadingModal: React.FC<{
   isVisible: boolean;
   session?: any;
   message?: string;
 }> = ({ isVisible, session, message }) => {
   if (!isVisible) return null;

   return (
     <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
       <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 transform scale-100 animate-pulse">
         <div className="text-center">
           <div className="relative w-16 h-16 mx-auto mb-6">
             <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
             <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 border-r-purple-600 animate-spin"></div>
             <div className="absolute inset-2 rounded-full border-2 border-transparent border-b-pink-500 animate-spin animate-reverse"></div>
           </div>

           <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
             {session ? '🔄 Reprise de session' : '🚀 Démarrage session'}
           </h2>

           {session && (
             <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-4 border border-blue-100">
               <div className="space-y-2 text-sm">
                 <div className="flex justify-between">
                   <span className="text-gray-600">📅 Date:</span>
                   <span className="font-bold text-gray-800">{session.audit_date}</span>
                 </div>
                 <div className="flex justify-between">
                   <span className="text-gray-600">👤 Agent:</span>
                   <span className="font-bold text-gray-800">{session.agent_email}</span>
                 </div>
                 <div className="flex justify-between">
                   <span className="text-gray-600">📊 Progression:</span>
                   <span className="font-bold text-green-600">
                     {session.completed_tasks}/{session.total_tasks}
                   </span>
                 </div>
                 <div className="flex justify-between">
                   <span className="text-gray-600">✅ Validées:</span>
                   <span className="font-bold text-green-600">{session.approved_tasks}</span>
                 </div>
                 <div className="flex justify-between">
                   <span className="text-gray-600">💬 Commentées:</span>
                   <span className="font-bold text-orange-600">{session.rejected_tasks}</span>
                 </div>
               </div>
               
               <div className="mt-3">
                 <div className="flex justify-between text-xs text-gray-600 mb-1">
                   <span>Progression</span>
                   <span>{Math.round((session.completed_tasks / session.total_tasks) * 100)}%</span>
                 </div>
                 <div className="w-full bg-gray-200 rounded-full h-2">
                   <div 
                     className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-1000"
                     style={{ width: `${(session.completed_tasks / session.total_tasks) * 100}%` }}
                   />
                 </div>
               </div>
             </div>
           )}

           <p className="text-gray-600 mb-2">
             {message || (session ? 'Chargement de votre session en cours...' : 'Préparation de votre session d\'audit...')}
           </p>
           
           <div className="flex justify-center space-x-1">
             <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
             <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
             <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
             </div>
         </div>
       </div>
     </div>
   );
 };

 if (!sessionStarted) {
   return (
     <>
       <LoadingModal 
         isVisible={loading || checkingPendingSession} 
         session={pendingSession}
         message={
           checkingPendingSession ? "Vérification session en cours..." :
           loading ? "Récupération des données..." : 
           undefined
         }
       />

       <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4 overflow-y-auto">
         <div className="max-w-6xl mx-auto">
           {/* Header */}
           <div className="text-center mb-8">
             <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
               Interface d'Audit Focal
             </h1>
             <p className="text-lg text-gray-600">Système d'audit des tâches agents - API Redash + Navigation Plano Multiple</p>
             
             {/* Status */}
             <div className="flex items-center justify-center gap-6 mt-3">
               <div className="flex items-center gap-2">
                 <div className={`w-3 h-3 rounded-full ${
                   connectionStatus === 'connected' ? 'bg-green-500' : 
                   connectionStatus === 'disconnected' ? 'bg-red-500' : 'bg-yellow-500'
                 }`}></div>
                 <span className="text-sm text-gray-600">
                   API: {connectionStatus === 'connected' ? 'Connectée' : 
                         connectionStatus === 'disconnected' ? 'Déconnectée' : 'Test...'}
                 </span>
               </div>

               <div className="flex items-center gap-2">
                 <div className={`w-3 h-3 rounded-full ${
                   appwriteStatus === 'connected' ? 'bg-green-500' : 
                   appwriteStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500'
                 }`}></div>
                 <span className="text-sm text-gray-600">
                   DB: {appwriteStatus === 'connected' ? 'Connecté' : 
                             appwriteStatus === 'error' ? 'Erreur' : 'Vérification...'}
                 </span>
                 {currentUser && (
                   <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                     {currentUser.profile.full_name}
                   </span>
                 )}
               </div>
             </div>
           </div>

           {/* ✅ SESSION EN ATTENTE - Affichage prioritaire */}
           {pendingSession ? (
             <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-6 mb-6">
               <div className="flex items-start gap-4">
                 <div className="bg-orange-100 p-3 rounded-full">
                   <RefreshCw className="w-6 h-6 text-orange-600" />
                 </div>
                 <div className="flex-1">
                   <h2 className="text-xl font-bold text-orange-800 mb-2">
                     🔄 Session d'audit en cours
                   </h2>
                   <div className="grid grid-cols-2 gap-4 mb-4">
                     <div>
                       <span className="text-orange-600 font-medium">📅 Date:</span>
                       <span className="ml-2 font-bold">{pendingSession.audit_date}</span>
                     </div>
                     <div>
                       <span className="text-orange-600 font-medium">👤 Agent:</span>
                       <span className="ml-2 font-bold">{pendingSession.agent_email}</span>
                     </div>
                     <div>
                       <span className="text-orange-600 font-medium">📊 Progression:</span>
                       <span className="ml-2 font-bold text-green-600">
                         {pendingSession.completed_tasks}/{pendingSession.total_tasks}
                       </span>
                     </div>
                     <div>
                       <span className="text-orange-600 font-medium">📝 Type:</span>
                       <span className="ml-2 font-bold">
                         {pendingSession.session_type === 'pair_only' ? 'Pair Only' : 'Complete Match'}
                       </span>
                     </div>
                   </div>

                   <div className="mb-4">
                     <div className="flex justify-between text-sm text-gray-600 mb-1">
                       <span>Progression</span>
                       <span>{Math.round((pendingSession.completed_tasks / pendingSession.total_tasks) * 100)}%</span>
                     </div>
                     <div className="w-full bg-gray-200 rounded-full h-3">
                       <div 
                         className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-500"
                         style={{ width: `${(pendingSession.completed_tasks / pendingSession.total_tasks) * 100}%` }}
                       />
                     </div>
                   </div>

                   <div className="flex gap-4">
                     <button
                       onClick={startAuditSession}
                       disabled={loading || checkingPendingSession}
                       className="bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3 px-6 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50"
                     >
                       {loading ? (
                         <div className="flex items-center gap-2">
                           <RefreshCw className="w-4 h-4 animate-spin" />
                           Reprise en cours...
                         </div>
                       ) : (
                         <div className="flex items-center gap-2">
                           <Play className="w-4 h-4" />
                           Reprendre la session
                         </div>
                       )}
                     </button>
                   </div>
                 </div>
               </div>
             </div>
           ) : (
             /* ✅ CONFIGURATION NORMALE */
             <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
               <h2 className="text-xl font-bold mb-4">Configuration</h2>
               
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                 <div className="space-y-4">
                   <div>
                     <label className="block font-bold text-gray-700 mb-2">Type de tâches</label>
                     <select
                       value={taskType}
                       onChange={(e) => setTaskType(e.target.value as any)}
                       className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                     >
                       <option value="pair_only">Pair Only Tasks</option>
                       <option value="complete_match">Complete Match Tasks</option>
                     </select>
                   </div>

                   <div>
                     <label className="block font-bold text-gray-700 mb-2">Date</label>
                     <input
                       type="date"
                       value={date}
                       onChange={(e) => setDate(e.target.value)}
                       className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                     />
                   </div>

                   <div className="bg-gray-50 rounded-lg p-4 border">
                     <div className="text-sm text-gray-600">
                       <div className="font-bold text-gray-800 mb-2">📊 Statistiques</div>
                       <div>• Total agents: {agents.length}</div>
                       <div>• Agents trouvés: {filteredAgents.length}</div>
                       <div>• Agent sélectionné: {selectedAgent ? '✓' : '✗'}</div>
                       <div>• API: {connectionStatus === 'connected' ? '✅' : '❌'}</div>
                       <div>• DB: {appwriteStatus === 'connected' ? '✅' : '❌'}</div>
                     </div>
                   </div>
                 </div>

                 <div>
                   <label className="block font-bold text-gray-700 mb-2">Agent à auditer</label>
                   
                   {loadingAgents ? (
                     <div className="w-full px-6 py-4 bg-gray-100 border-2 border-gray-300 rounded-xl flex items-center gap-3">
                       <RefreshCw className="w-5 h-5 animate-spin text-gray-500" />
                       <span className="text-gray-500">Chargement agents depuis API...</span>
                     </div>
                   ) : (
                     <div className="space-y-3">
                       <div className="relative">
                         <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                         <input
                           type="text"
                           placeholder="Rechercher un agent (email, nom, ID)..."
                           value={agentSearch}
                           onChange={(e) => setAgentSearch(e.target.value)}
                           className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                         />
                       </div>

                       <div className="border rounded-lg bg-white max-h-64 overflow-y-auto">
                       {filteredAgents.length === 0 ? (
                           <div className="p-4 text-center text-gray-500">
                             <Users className="w-6 h-6 mx-auto mb-2 opacity-50" />
                             <p>Aucun agent trouvé</p>
                           </div>
                         ) : (
                           <div className="p-2 space-y-1">
                             {filteredAgents.map((agent) => (
                               <button
                                 key={agent.email}
                                 onClick={() => setSelectedAgent(agent.email)}
                                 className={`w-full text-left p-2 rounded-lg transition-colors ${
                                   selectedAgent === agent.email
                                     ? 'bg-blue-100 border-2 border-blue-300 text-blue-800'
                                     : 'hover:bg-gray-50 border-2 border-transparent'
                                 }`}
                               >
                                 <div className="flex justify-between items-center">
                                   <div>
                                     <div className="font-bold text-sm">{agent.displayName}</div>
                                     <div className="text-xs text-gray-600">{agent.email}</div>
                                   </div>
                                   <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                     {agent.userId}
                                   </div>
                                 </div>
                               </button>
                             ))}
                           </div>
                         )}
                       </div>

                       {selectedAgent && (
                         <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                           <div className="flex items-center gap-2">
                             <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                               {selectedAgent.match(/agent-(\d+)@/)?.[1] || selectedAgent.charAt(0).toUpperCase()}
                             </div>
                             <div>
                               <div className="font-bold text-blue-800 text-sm">
                                 {agents.find(a => a.email === selectedAgent)?.displayName}
                               </div>
                               <div className="text-xs text-blue-600">{selectedAgent}</div>
                             </div>
                           </div>
                         </div>
                       )}
                     </div>
                   )}
                 </div>
               </div>

               {error && (
                 <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                   <div className="flex items-center gap-2">
                     <AlertTriangle className="w-4 h-4 text-red-500" />
                     <span className="text-red-700 font-medium">Erreur</span>
                   </div>
                   <p className="text-red-600 text-sm mt-1">{error}</p>
                 </div>
               )}

               <div className="flex items-center justify-center gap-4 mt-6">
                 <button
                   onClick={() => {
                     if (onBackToDashboard) {
                       onBackToDashboard();
                     }
                   }}
                   className="bg-gradient-to-r from-red-500 to-red-600 text-white font-bold py-3 px-6 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 hover:from-red-600 hover:to-red-700"
                 >
                   <div className="flex items-center gap-2">
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                     </svg>
                     Dashboard
                   </div>
                 </button>

                 <button
                   onClick={startAuditSession}
                   disabled={loading || !selectedAgent || connectionStatus === 'disconnected' || appwriteStatus !== 'connected'}
                   className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                 >
                   {loading ? (
                     <div className="flex items-center gap-2">
                       <RefreshCw className="w-4 h-4 animate-spin" />
                       Création session...
                     </div>
                   ) : (
                     <div className="flex items-center gap-2">
                       <Play className="w-4 h-4" />
                       <Database className="w-4 h-4" />
                       Démarrer nouvel audit
                     </div>
                   )}
                 </button>
               </div>
             </div>
           )}

           {/* Instructions */}
           <div className="bg-white rounded-xl shadow-lg p-6">
             <h3 className="text-lg font-bold mb-4">✅ Instructions - Issues Labels CORRIGÉS + Multiple Plano SUPPORTÉ</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
               <div>
                 <h4 className="font-bold text-blue-600 mb-2">🔷 Pair Only Tasks</h4>
                 <ul className="space-y-1 text-gray-600">
                   <li>• 3 images: Model, Agent Template, Plano</li>
                   <li>• ✅ <strong>CORRIGÉ:</strong> Affichage issue_labels</li>
                   <li>• ✅ Support plano multiples</li>
                 </ul>
               </div>
               <div>
                 <h4 className="font-bold text-purple-600 mb-2">🔷 Complete Match Tasks</h4>
                 <ul className="space-y-1 text-gray-600">
                   <li>• 2 images: Template et Plano</li>
                   <li>• ✅ Affichage issue_names (déjà OK)</li>
                   <li>• ✅ Support plano multiples</li>
                 </ul>
               </div>
             </div>
             <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
               <h4 className="font-bold text-green-800 mb-1">🔧 Corrections apportées:</h4>
               <ul className="text-sm text-green-700 space-y-1">
                 <li>• <strong>parseAuditDataWithFixedExtraction():</strong> Utilise issue_labels pour Pair Only, issue_names pour Complete Match</li>
                 <li>• <strong>PlanoImageViewer:</strong> Navigation multiple images pour TOUS les types de tâches</li>
                 <li>• <strong>TaskCard:</strong> Affichage visible des issues pour les auditeurs</li>
                 <li>• <strong>extractImageUrlsFixed():</strong> Support extraction multiple URLs</li>
                 <li>• <strong>ImageViewer:</strong> Correction boucle d'erreur images</li>
               </ul>
             </div>
           </div>
         </div>
       </div>
     </>
   );
 }

 const currentTask = tasks[currentTaskIndex];

 return (
   <>
     <LoadingModal 
       isVisible={loading} 
       session={currentSession}
       message={loading ? "Récupération des données..." : undefined}
     />

     <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-2 overflow-y-auto">
       <div className="max-w-[98vw] mx-auto">
         {/* Header de session - COMPACT avec Appwrite */}
         <div className="bg-white rounded-xl shadow-lg p-3 mb-4">
           <div className="flex items-center justify-between">
             <div className="flex items-center gap-3">
               <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-2 rounded-lg">
                 <Eye className="w-5 h-5" />
               </div>
               <div>
                 <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                   Session API - {taskType === 'pair_only' ? 'Pair Only' : 'Complete Match'}
                   {currentSession && (
                     <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                       Session #{currentSession.$id?.slice(-8)}
                     </span>
                   )}
                 </h1>
                 <p className="text-sm text-gray-600 flex items-center gap-4">
                   <span>Tâche {currentTaskIndex + 1}/{tasks.length}</span>
                   <span>• {selectedAgent}</span>
                   <span>• {date}</span>
                   {currentSession && (
                     <>
                       <span>• ✅ {currentSession.approved_tasks} validées</span>
                       <span>• 💬 {currentSession.rejected_tasks} commentées</span>
                     </>
                   )}
                 </p>
               </div>
             </div>
             
             <div className="flex items-center gap-3">
               {savingToAppwrite && (
                 <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-lg">
                   <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                   <span className="text-blue-600 text-sm font-medium">Sauvegarde...</span>
                 </div>
               )}               
               
               <button
                 onClick={handleLogout}
                 className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-sm font-bold transition-colors"
               >
                 <LogOut className="w-3 h-3" />
                 Quitter
               </button>
             </div>
           </div>
         </div>

         {/* Tâche actuelle - PLEINE HAUTEUR avec KEY FIX */}
         <div className="space-y-4">
           {tasks.map((task, index) => (
             <div 
               key={`task-${task.match_task_id}-${index}-${task.appwrite_task_id || 'no-id'}`} 
               className={`transition-all duration-500 ${
                 index === currentTaskIndex ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none absolute inset-0'
               }`}
             >
               <TaskCard
                 task={task}
                 onValidate={handleValidate}
                 onComment={handleComment}
                 isCurrentTask={index === currentTaskIndex}
                 savingToAppwrite={savingToAppwrite}
               />
             </div>
           ))}
         </div>

         {/* Résumé des audits - OVERLAY avec données Appwrite */}
         {(auditResults.length > 0 || currentSession) && (
           <div className="fixed bottom-4 right-4 bg-white rounded-xl shadow-xl p-4 border border-gray-200 max-w-sm">
             <h3 className="font-bold text-gray-800 mb-2 text-sm flex items-center gap-2">
               <Database className="w-4 h-4 text-green-600" />
               Résumé Session
             </h3>
             <div className="grid grid-cols-2 gap-2 text-xs">
               <div className="bg-green-50 rounded p-2 text-center border border-green-200">
                 <div className="font-bold text-green-600">
                   {currentSession?.approved_tasks || auditResults.filter(r => r.result === 'validated').length}
                 </div>
                 <div className="text-green-700">Validées</div>
               </div>
               <div className="bg-orange-50 rounded p-2 text-center border border-orange-200">
                 <div className="font-bold text-orange-600">
                   {currentSession?.rejected_tasks || auditResults.filter(r => r.result === 'commented').length}
                 </div>
                 <div className="text-orange-700">Commentées</div>
               </div>
             </div>
             
             <div className="mt-2 pt-2 border-t text-xs">
               <div className="flex justify-between text-gray-600 mb-1">
                 <span>Progression:</span>
                 <span>{currentSession?.completed_tasks || 0}/{currentSession?.total_tasks || tasks.length}</span>
               </div>
               <div className="w-full bg-gray-200 rounded-full h-1.5">
                 <div 
                   className="bg-gradient-to-r from-blue-500 to-purple-500 h-1.5 rounded-full transition-all duration-300"
                   style={{ 
                     width: `${currentSession ? (currentSession.completed_tasks / currentSession.total_tasks) * 100 : 0}%` 
                   }}
                 />
               </div>
             </div>

             {auditResults.some(r => r.result === 'commented') && (
               <div className="mt-2 pt-2 border-t text-xs">
                 <div className="flex gap-1">
                   {['minor', 'major', 'critical'].map(severity => {
                     const count = auditResults.filter(r => r.severity === severity).length;
                     const colors = {
                       minor: 'bg-yellow-100 text-yellow-800',
                       major: 'bg-orange-100 text-orange-800', 
                       critical: 'bg-red-100 text-red-800'
                     };
                     return count > 0 ? (
                       <span key={severity} className={`px-1 py-0.5 rounded ${colors[severity as keyof typeof colors]}`}>
                         {severity}: {count}
                       </span>
                     ) : null;
                   })}
                 </div>
               </div>
             )}

             {currentSession && (
               <div className="mt-2 pt-2 border-t">
                 <div className="flex items-center gap-2 text-xs">
                   <div className={`w-2 h-2 rounded-full ${
                     currentSession.session_status === 'in_progress' ? 'bg-green-500' :
                     currentSession.session_status === 'completed' ? 'bg-blue-500' : 'bg-yellow-500'
                   }`}></div>
                   <span className="text-gray-600 capitalize">{currentSession.session_status}</span>
                   <Save className="w-3 h-3 text-green-600 ml-auto" />
                 </div>
               </div>
             )}
           </div>
         )}
       </div>
     </div>
   </>
 );
};

export default BeautifulAuditInterface;